<script lang="ts">
	import { beforeUpdate } from 'svelte';
	// Stores
	import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { format } from 'svelte-i18n';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	// Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { status_configurator } from 'src/helpers/device_data_helpers';

	// Design System Components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';
	// Helpers
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Components
	import DeviceDrawerUnsupportedFw from 'src/components/Drawer/DeviceDrawer/DeviceDrawerUnsupportedFW.svelte';
	import ThumbLayerChart from 'src/components/Chart/LayerChart/ThumbLayerChart.svelte';
	import PeakPowerInfoV2 from 'src/components/Consumption/PeakPowerInfoV2.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';

	// Get device data from sidepanel state
	export let device: Device;

	const deviceConfig = devices_config?.[device?.type];

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	// Status and label processing
	$: _statuses = status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: labelList = Array.isArray(_statuses)
		? _statuses.filter((e) => {
				return 'undefined' === typeof e.main && e.name !== 'state';
			})
		: [];
	$: combinedLabelList = labelList;
	$: powerStatus = Array.isArray(_statuses)
		? _statuses.filter((s) => s.name === 'power')[0]
		: undefined;

	// Charts and firmware
	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	const handleOnClickThumbChart = (event: CustomEvent | Event) => {
		navigateToHistoryStatisticsTab();
	};

	let timerLabels: Record<string, any> = {};
	let deviceState: any = getDeviceState(device?.id);

	beforeUpdate(() => {
		deviceState = getDeviceState(device?.id);
	});
</script>

<DeviceViewStateWrapper {device}>
	<div class="label-list-wrapper mb-s" slot="staticTop">
		<DeviceLabelList labelList={combinedLabelList} />
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<!-- staticBottom content -->
	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<div class="flex-full-height justify-content-end">
			{#if deviceChartsConfig.length}
				<ThumbLayerChart {device} on:click={handleOnClickThumbChart} />
			{/if}

			<PeakPowerInfoV2 {device} />
			{#if powerStatus}
				<ParameterCardV2
					backgroundColor="--color-0"
					size="s"
					class="mb-s"
					icon={mapIconToThin('fas fa-plug')}
					title={powerStatus?.value}
					description={capFirstChar($format('collapse_title_power', { default: 'Power' }))}
				/>
			{/if}
		</div>
	</div>
</DeviceViewStateWrapper>
